import React, { useId } from "react";
import TextField from "@mui/material/TextField";
import "./InputFields.css";

const mergeRefs = (...refs) => (node) => {
  refs.forEach((r) => {
    if (typeof r === "function") r(node);
    else if (r && typeof r === "object") r.current = node;
  });
};

function stripNonDomProps(rest) {
  if (!rest || typeof rest !== "object") return {};
  const {
    variant,
    fullWidth,
    error: _e,
    helperText: _h,
    InputProps: _ip,
    inputProps: _inp,
    InputLabelProps: _ilp,
    margin,
    sx: _sx,
    SelectProps: _sp,
    label: _l,
    ...dom
  } = rest;
  return dom;
}

const InputText = React.forwardRef(
  (
    {
      name,
      label,
      value,
      placeholder,
      onChange,
      onBlur,
      error,
      errorBorderOnly = false,
      helperText,
      endAdornment,
      startAdornment,
      fieldHeight,
      select,
      children,
      defaultValue,
      disable,
      disabled,
      autoComplete = "off",
      isRight,
      showErrorOnly,
      inputRef,
      sx: _sxProp,
      multiline,
      fullWidth,
      size,
      InputProps,
      inputProps: muiInputProps,
      InputLabelProps,
      className,
      type,
      style: rootStyleProp,
      rows,
      minRows,
      maxRows,
      id: idProp,
      readOnly,
      required,
      /** When `label` is empty, exposes the field to assistive tech */
      ariaLabel,
      SelectProps,
      ...rest
    },
    ref
  ) => {
    const uid = useId();
    const inputId = idProp || name || `cs-input-${uid}`;
    // Project-wide rule: never surface inline error text below a field — the
    // red border/shell is the only error affordance. Hint helperText (when
    // there is no error) still renders for fields that need guidance copy.
    const displayHelperText = error || showErrorOnly ? undefined : helperText;
    const finalInputRef = inputRef || ref;
    /* readOnly must not use disabled shell (#e2e8f0) — e.g. fake dropdowns that open on toggle */
    const isDisabled = Boolean(disable || disabled);

    /* label="Field *" → strip star and show red .cs-field-label-required (same as required prop) */
    let displayLabel = label;
    let effectiveRequired = Boolean(required);
    if (typeof label === "string") {
      const stripped = label.replace(/\s*\*\s*$/, "");
      if (stripped !== label) {
        displayLabel = stripped;
        effectiveRequired = true;
      }
    }

    const muiStart = InputProps?.startAdornment;
    const muiEnd = InputProps?.endAdornment;
    const finalStart = startAdornment ?? muiStart;
    const finalEnd = endAdornment ?? muiEnd;

    /* App standard row height — do not vary by `size` or Title vs Plan Details mismatch. */
    const height = fieldHeight || "44px";

    /* `type="textarea"` without `multiline` used to render a tall <input> — placeholder looked vertically centered */
    const isMultiline = Boolean(multiline || type === "textarea");

    const hasErr = Boolean(error);
    const shellClass = [
      "cs-input-shell",
      hasErr ? "cs-input-shell--error" : "",
      isMultiline ? "cs-input-shell--multiline" : "",
      isDisabled ? "cs-input-shell--disabled" : "",
    ]
      .filter(Boolean)
      .join(" ");

    const inputClass = [
      "cs-input",
      isRight ? "cs-input--right" : "",
      hasErr && !errorBorderOnly ? "cs-input--error-text" : "",
    ]
      .filter(Boolean)
      .join(" ");

    const mergedInputProps = { ...(muiInputProps || {}) };
    const {
      ref: muiInputRef,
      className: muiInputClass,
      style: muiInputStyle,
      onChange: muiInputOnChange,
      onBlur: muiInputOnBlur,
      value: muiValue,
      ...restMergedInput
    } = mergedInputProps;

    const domRest = stripNonDomProps(rest);

    const handleInnerChange = (e) => {
      muiInputOnChange?.(e);
      let inputValue = e.target.value;
      if (inputValue?.length === 1 && inputValue?.startsWith(" ")) {
        inputValue = "";
      }
      if (onChange) {
        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            name: name || e.target.name,
            value: inputValue,
          },
          currentTarget: {
            ...e.currentTarget,
            name: name || e.target.name,
            value: inputValue,
          },
        };
        onChange(syntheticEvent);
      }
    };

    const handleInnerBlur = (e) => {
      muiInputOnBlur?.(e);
      onBlur?.(e);
    };

    const controlledValue =
      value !== undefined
        ? value
        : muiValue !== undefined
          ? muiValue
          : undefined;

    const derivedPlaceholder =
      !select && label
        ? `Enter ${String(label).replace(/^Enter\s+/i, "").trim()}`
        : undefined;
    const effectivePlaceholder = placeholder ?? derivedPlaceholder;

    const commonInputProps = {
      id: inputId,
      name: name || rest.name,
      placeholder: effectivePlaceholder,
      disabled: isDisabled,
      autoComplete: autoComplete || "off",
      className: [inputClass, muiInputClass].filter(Boolean).join(" "),
      style: {
        ...(muiInputStyle || {}),
      },
      onChange: handleInnerChange,
      onBlur: handleInnerBlur,
      "aria-required": effectiveRequired ? true : undefined,
      ...restMergedInput,
    };
    if (readOnly && !isDisabled) {
      commonInputProps.readOnly = true;
    }
    if (ariaLabel && !label) {
      commonInputProps["aria-label"] = ariaLabel;
    }

    if (!select) {
      if (isMultiline) {
        commonInputProps.rows =
          rows || minRows || maxRows ? rows || minRows || 4 : 4;
      } else {
        commonInputProps.type = type || "text";
      }
    }

    if (controlledValue !== undefined) {
      commonInputProps.value = controlledValue;
    } else if (defaultValue !== undefined) {
      commonInputProps.defaultValue = defaultValue;
    }

    const shellStyle = {
      ...(isMultiline ? { minHeight: height } : { minHeight: height, height }),
      width: fullWidth ? "100%" : undefined,
      ...(typeof InputProps?.style === "object" && InputProps.style !== null
        ? InputProps.style
        : {}),
      ...rootStyleProp,
    };

    const fieldWrapClass = [
      "cs-form-field",
      fullWidth ? "cs-form-field--full" : "",
      className || "",
    ]
      .filter(Boolean)
      .join(" ");

    /* Native <select> cannot host MUI <MenuItem />. Pass SelectProps={{}} (or any object) so we use MUI TextField select. */
    const useMuiSelect =
      Boolean(select && SelectProps && SelectProps.native !== true);

    const shellClassMerged = [
      shellClass,
      !useMuiSelect && InputProps?.className ? InputProps.className : "",
    ]
      .filter(Boolean)
      .join(" ");

    const mergedSelectProps = useMuiSelect
      ? {
          ...SelectProps,
          MenuProps: {
            disablePortal: false,
            ...SelectProps?.MenuProps,
            PaperProps: {
              ...SelectProps?.MenuProps?.PaperProps,
              sx: {
                zIndex: 1400,
                ...SelectProps?.MenuProps?.PaperProps?.sx,
              },
            },
          },
        }
      : undefined;

    const muiSelectInputProps = useMuiSelect
      ? {
          ...InputProps,
          ...(finalStart ? { startAdornment: finalStart } : {}),
          ...(finalEnd ? { endAdornment: finalEnd } : {}),
          notched: false,
        }
      : undefined;

    const textFieldValueProps =
      controlledValue !== undefined
        ? { value: controlledValue }
        : defaultValue !== undefined
          ? { defaultValue }
          : { value: "" };

    const inputNode = useMuiSelect ? (
      <TextField
        select
        hiddenLabel={Boolean(label)}
        id={inputId}
        name={name}
        {...textFieldValueProps}
        onChange={handleInnerChange}
        onBlur={handleInnerBlur}
        disabled={isDisabled}
        error={hasErr}
        helperText={displayHelperText}
        required={effectiveRequired}
        fullWidth={fullWidth !== false}
        placeholder={effectivePlaceholder}
        variant="outlined"
        size={size === "small" ? "small" : "medium"}
        inputRef={mergeRefs(finalInputRef, muiInputRef)}
        InputLabelProps={InputLabelProps}
        SelectProps={mergedSelectProps}
        InputProps={muiSelectInputProps}
        inputProps={{
          ...muiInputProps,
          "aria-label": name || undefined,
        }}
        sx={{
          width:
            rootStyleProp && typeof rootStyleProp === "object" && rootStyleProp.width
              ? rootStyleProp.width
              : "100%",
          "& .MuiOutlinedInput-root": {
            minHeight: height,
            borderRadius: "8px",
            alignItems: "center",
            backgroundColor: "#F7F8FA",
            transition: "background-color 0.15s ease, box-shadow 0.15s ease",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#E5E7EB",
            },
            "&:hover:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline":
              {
                borderColor: "#D1D5DB",
              },
            "&.Mui-focused:not(.Mui-error)": {
              backgroundColor: "#FFFFFF",
              boxShadow: "var(--focus-ring)",
            },
            "&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
              borderColor: "#2563EB",
            },
            "&.Mui-disabled": {
              backgroundColor: "#e2e8f0",
            },
          },
          "& .MuiOutlinedInput-root.Mui-error.Mui-focused": {
            boxShadow: "var(--focus-ring-error)",
          },
          "& .MuiSelect-select": {
            display: "flex",
            alignItems: "center",
            minHeight: height,
            boxSizing: "border-box",
            padding: "10px 14px",
          },
          ...(typeof _sxProp === "object" && _sxProp !== null ? _sxProp : {}),
        }}
        style={
          rootStyleProp && typeof rootStyleProp === "object"
            ? { ...rootStyleProp, width: undefined }
            : undefined
        }
      >
        {children}
      </TextField>
    ) : select ? (
      <select
        {...domRest}
        {...commonInputProps}
        ref={mergeRefs(finalInputRef, muiInputRef)}
      >
        {children}
      </select>
    ) : isMultiline ? (
      <textarea
        {...domRest}
        {...commonInputProps}
        ref={mergeRefs(finalInputRef, muiInputRef)}
      />
    ) : (
      <input
        {...domRest}
        {...commonInputProps}
        ref={mergeRefs(finalInputRef, muiInputRef)}
      />
    );

    return (
      <div
        className={fieldWrapClass}
        style={{
          width: fullWidth ? "100%" : undefined,
        }}
      >
        {displayLabel != null && displayLabel !== "" ? (
          <label
            htmlFor={inputId}
            className={`cs-field-label ${hasErr && !errorBorderOnly ? "cs-field-label--error" : ""}`}
            {...(InputLabelProps || {})}
          >
            {displayLabel}
            {effectiveRequired ? (
              <span className="cs-field-label-required" aria-hidden="true">
                {" "}
                *
              </span>
            ) : null}
          </label>
        ) : null}
        {useMuiSelect ? (
          inputNode
        ) : (
          <div
            ref={InputProps?.ref}
            className={shellClassMerged}
            style={shellStyle}
            onClick={InputProps?.onClick}
          >
            {finalStart ? (
              <span className="cs-input-adorn cs-input-adorn--start">
                {finalStart}
              </span>
            ) : null}
            {inputNode}
            {finalEnd ? (
              <span className="cs-input-adorn cs-input-adorn--end">
                {finalEnd}
              </span>
            ) : null}
          </div>
        )}
        {!useMuiSelect && hasErr && displayHelperText ? (
          <div className="cs-field-helper cs-field-helper--error" role="alert">
            {displayHelperText}
          </div>
        ) : !useMuiSelect && displayHelperText ? (
          <div className="cs-field-helper">{displayHelperText}</div>
        ) : null}
      </div>
    );
  }
);

export default InputText;
